import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { subscribeDoseGapPrompt } from '../../utils/helper/doseGapPrompt';

/**
 * Confirmation shown when a dose is handed over before the prescription's
 * required separation has elapsed.
 *
 * Every string comes from the backend's dose_gap_prompt, so the wording and the
 * rule stay in one place. A reason is optional: the backend records who
 * delivered early and by how much either way, and demanding a sentence at the
 * counter only earns "n/a".
 *
 * Built like the handover dialog, portal included. Rendered in place it was
 * invisible: this component is mounted first in the app tree, its z-index class
 * resolved to auto in the built stylesheet, and the whole page therefore painted
 * over a full-screen overlay that had been there the entire time. A portal to
 * document.body puts it last in the document, and the z-index is set inline so
 * the CSS build cannot drop it again.
 */
const DoseGapOverrideModal = () => {
    const [prompt, setPrompt] = useState(null);
    const resolverRef = useRef(null);

    useEffect(() => subscribeDoseGapPrompt((nextPrompt, resolve) => {
        resolverRef.current = resolve;
        setPrompt(nextPrompt);
    }), []);

    const close = (result) => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        setPrompt(null);
        if (resolve) resolve(result);
    };

    // Deliberately no body scroll lock, and one that clears any lock left
    // behind. Locking the body stopped the page scrolling behind the dialog, but
    // every path that skipped the cleanup - an unmount mid-flight, another
    // modal restoring its own captured value - left the whole page frozen with
    // no way back. A background that scrolls a little is not worth a driver
    // stranded on a dead screen.
    useEffect(() => {
        if (!prompt) return;
        if (document.body.style.overflow === 'hidden') {
            document.body.style.overflow = '';
        }
    }, [prompt]);

    // Escape is a deliberate key press, unlike a stray tap on the backdrop, so
    // it counts as the cancel button.
    useEffect(() => {
        if (!prompt) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close({ confirmed: false, reason: '' });
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [prompt]);

    if (!prompt) return null;

    // The backend used to send each row as a sentence and now sends it split.
    // The apps and the servers deploy separately, so both shapes are accepted -
    // otherwise a new app against an older server shows an empty dialog.
    const details = (Array.isArray(prompt.details) ? prompt.details : [])
        .map((row) => (typeof row === 'string' ? { label: row, value: '' } : row))
        .filter((row) => row && (row.label || row.value));

    // Absent means overridable, so an older payload still behaves as before.
    const canOverride = prompt.can_override !== false;
    const title = prompt.title || 'Too early for this dose';
    const confirmLabel = prompt.confirm_label || 'Deliver Anyway';
    const cancelLabel = prompt.cancel_label || (canOverride ? 'Cancel' : 'Close');
    const cancel = () => close({ confirmed: false, reason: '' });

    // Red when the dose is refused outright, yellow when staff can still go
    // ahead, so the state reads before a word of it does.
    //
    // Classes are Tailwind 2 only: the app loads a prebuilt Tailwind 2.2.19
    // stylesheet from public/index.html, so v3 colours (amber) and arbitrary
    // values (min-h-[44px], z-[100]) do not exist and silently do nothing.
    // Anything else is set inline.
    const tone = canOverride
        ? { icon: 'text-yellow-600', chip: 'bg-yellow-50 text-yellow-800', tile: 'border-yellow-200 bg-yellow-50', value: 'text-yellow-800' }
        : { icon: 'text-red-600', chip: 'bg-red-50 text-red-700', tile: 'border-red-200 bg-red-50', value: 'text-red-700' };

    return createPortal(
        <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dose-gap-title"
        >
            {/* A dose decision should not be dismissed by a stray tap, so this
                backdrop is inert - unlike the handover dialog, the buttons are
                the only way out. */}
            <div
                className="absolute inset-0"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)' }}
                aria-hidden="true"
            />

            {/* Same frame as the app's other dialogs (routeConfirmation,
                defaultPharmacistModal): small bold title in a header bar over
                a dashed rule, grey close icon, body underneath. */}
            <div
                className="relative flex w-full max-w-md flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white"
                style={{ maxHeight: 'calc(100vh - 2rem)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)' }}
            >
                <div className="flex items-center justify-between rounded-t border-b border-dashed p-2 px-4 md:p-3">
                    <div className="flex items-center gap-2">
                        <svg aria-hidden="true" className={`h-5 w-5 flex-shrink-0 ${tone.icon}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9" />
                            <path d="M12 7v5l3 2" />
                        </svg>
                        <h3 id="dose-gap-title" className="text-sm font-bold text-gray-900">{title}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={cancel}
                        className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900"
                    >
                        <svg aria-hidden="true" className="h-3 w-3" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M1 1l12 12M13 1L1 13" />
                        </svg>
                        <span className="sr-only">Close modal</span>
                    </button>
                </div>

                <div className="p-4 md:p-5">
                    {prompt.message ? (
                        <div className={`inline-flex max-w-full rounded-full px-3 py-1 text-sm font-semibold ${tone.chip}`}>
                            <span className="truncate">{prompt.message}</span>
                        </div>
                    ) : null}

                    {/* The rows are the backend's own label/value pairs. The last
                        is when the dose becomes due - what staff act on - so it
                        carries the state colour. */}
                    {details.length > 0 ? (
                        <dl className={`mt-4 grid gap-3 ${details.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            {details.map((row, index) => {
                                const isKey = index === details.length - 1;
                                return (
                                    <div
                                        key={row.label}
                                        className={`rounded-md border p-3 ${isKey ? tone.tile : 'border-gray-100 bg-gray-50'}`}
                                    >
                                        <dt className="text-xs font-medium text-gray-500">{row.label}</dt>
                                        {row.value ? (
                                            <dd className={`mt-1 text-lg font-bold ${isKey ? tone.value : 'text-gray-900'}`}>{row.value}</dd>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </dl>
                    ) : null}

                    {/* Below the hard floor the backend refuses however the
                        request is sent, so there is no confirm button to offer -
                        showing one would promise something the server will not
                        do. Buttons match the app's own (defaultPharmacistModal);
                        Deliver Anyway is green-500 (#10b981 in Tailwind 2), the
                        colour of the Continue button it follows on from. */}
                    <div className={`mt-5 grid gap-3 ${canOverride ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        <button
                            type="button"
                            onClick={cancel}
                            autoFocus={!canOverride}
                            className={canOverride
                                ? 'flex w-full items-center justify-center rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-200'
                                : 'flex w-full items-center justify-center rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300'}
                        >
                            {cancelLabel}
                        </button>
                        {canOverride ? (
                            <button
                                type="button"
                                onClick={() => close({ confirmed: true, reason: '' })}
                                className="flex w-full items-center justify-center rounded-lg bg-green-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-green-600 focus:outline-none focus:ring-4 focus:ring-green-300"
                            >
                                {confirmLabel}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DoseGapOverrideModal;
